import { createAction, props } from '@ngrx/store';

import { DropDownList } from '@core/interfaces';

import { Columns, Filters, Grid, SelectedRow, SummaryRow, SummaryRowText } from '#types/grid';
import { GridLoadResponseBody, GridRemoveResponseBody, GridSaveConflictCheckResponseBody, GridSaveResponseBody } from '#types/grid';
import { ListOfValuesResponse } from '#types/list';

export const openSidebar = createAction('[Grid.Sidebar] - Open');
export const closeSidebar = createAction('[Grid.Sidebar] - Close');

export const initPanel = createAction('[Grid.Panel] - Init');
export const resetPanel = createAction('[Grid.Panel] - Reset');

export const openFilterPanel = createAction('[Grid.Panel.Filter] - Open');
export const closeFilterPanel = createAction('[Grid.Panel.Filter] - Close');

export const openColumnPanel = createAction('[Grid.Panel.Column] - Open');
export const closeColumnPanel = createAction('[Grid.Panel.Column] - Close');

export const openExportPanel = createAction('[Grid.Panel.Export] - Open');
export const closeExportPanel = createAction('[Grid.Panel.Export] - Close');

export const openSaveGridPanel = createAction('[Grid.Panel.Save] - Open');
export const closeSaveGridPanel = createAction('[Grid.Panel.Save] - Close');

export const openLoadGridPanel = createAction('[Grid.Panel.Load] - Open');
export const closeLoadGridPanel = createAction('[Grid.Panel.Load] - Close');

//
// grid instance actions
//

export const Initialize = createAction('[Grid] - Initialize', props<{ id: string; grid: Grid }>());
export const InitializeGridFromSync = createAction(
  '[Grid] - Initialize Grid From Sync',
  props<{ id: string; grid: Grid; defaults: Grid }>()
);
export const InitializeGridFromDefaults = createAction(
  '[Grid] - Initialize Grid From Defaults',
  props<{ id: string; grid: Grid; defaults: Grid }>()
);
export const InitializeGridComplete = createAction(
  '[Grid] - Initialize Grid Complete',
  props<{ id: string; grid: Grid; defaults: Grid }>()
);

export const Reset = createAction('[Grid] - Reset', props<{ id: string }>());
export const Destroy = createAction('[Grid] - Destroy', props<{ id: string }>());
export const DestroyMultiple = createAction('[Grid] - Destroy Multiple', props<{ list: string[] }>());

export const SetData = createAction('[Grid] - Set Data', props<{ id: string; data: any }>());

export const SetFilters = createAction('[Grid] - Set Filters', props<{ id: string; filters: Filters }>());
export const SetDefaultFilters = createAction('[Grid] - Set Default Filters', props<{ id: string; filters: Filters }>());
export const OverrideFilters = createAction('[Grid] - Override Filters', props<{ id: string; filters: Filters }>());

export const SetPage = createAction('[Grid] - Set Page and Rows', props<{ id: string; page: number; first: number; rows: number }>());
export const SetColumnSize = createAction('[Grid] - Set Column Size', props<{ id: string; column: string; size: number }>());
export const SetSummaryRow = createAction('[Grid] - Set Summary Row', props<{ id: string; summaryRow: SummaryRow }>());
export const SetSummaryRowText = createAction('[Grid] - Set Summary Row Text', props<{ id: string; summaryRowText: SummaryRowText }>());

export const SetColumns = createAction(
  '[Grid] - Set Columns',
  props<{ id: string; selectedColumns: Columns; availableColumns: Columns }>()
);

export const SelectRow = createAction('[Grid] - Select Row', props<{ id: string; selectedRow: SelectedRow<any> }>());
export const ClearSelectedRow = createAction('[Grid] - Clear Selected Row', props<{ id: string }>());

export const SelectMultipleRows = createAction('[Grid] - Select Multiple Rows', props<{ id: string; rows: Record<string, any>[] }>());
export const ClearMultipleRows = createAction('[Grid] - Clear Multiple Rows', props<{ id: string }>());

export const LoadSettings = createAction('[Grid] - Load Settings', props<{ id: string; saveId: string; saveName: string; grid: Grid }>());

//
// grid state save / load / remove
//

//
// list saved grids
//

export const ListRequest = createAction('[Grid] - List Request', props<{ id: string }>());
export const ListRequestSuccess = createAction('[Grid] - List Request Success', props<{ id: string; list: ListOfValuesResponse }>());
export const ListRequestFailure = createAction('[Grid] - List Request Failure', props<{ id: string }>());

//
// load grid
//

export const LoadRequest = createAction('[Grid] - Load Request', props<{ id: string; saveId: string; saveName: string }>());
export const LoadRequestSuccess = createAction(
  '[Grid] - Load Request Success',
  props<{ id: string; saveId: string; saveName: string; data: GridLoadResponseBody }>()
);
export const LoadRequestFailure = createAction('[Grid] - Load Request Failure', props<{ id: string; saveId: string }>());

//
// remove grid
//

export const RemoveRequest = createAction('[Grid] - Remove Request', props<{ id: string; saveId: string }>());
export const RemoveRequestSuccess = createAction(
  '[Grid] - Remove Request Success',
  props<{ id: string; saveId: string; data: GridRemoveResponseBody }>()
);
export const RemoveRequestFailure = createAction('[Grid] - Remove Request Failure', props<{ id: string; saveId: string }>());

//
// save grid
//

export const SaveRequest = createAction('[Grid] - Save Request', props<{ id: string; saveId: string; saveName: string }>());
export const SaveRequestSuccess = createAction(
  '[Grid] - Save Request Success',
  props<{ id: string; saveId: string; saveName: string; data: GridSaveResponseBody }>()
);
export const SaveRequestFailure = createAction('[Grid] - Save Request Failure', props<{ id: string; saveId: string; saveName: string }>());

//
// save conflict check grid
//

export const SaveConflictCheckRequest = createAction(
  '[Grid] - Save Conflict Check Request',
  props<{ id: string; saveId: string; saveName: string }>()
);
export const SaveConflictCheckRequestSuccess = createAction(
  '[Grid] - Save Conflict Check Request Success',
  props<{ id: string; saveId: string; saveName: string; data: GridSaveConflictCheckResponseBody }>()
);
export const SaveConflictCheckRequestFailure = createAction(
  '[Grid] - Save Conflict Check Request Failure',
  props<{ id: string; saveId: string; saveName: string }>()
);

//
// grid edit controls
//
export const SetDropdownListValues = createAction(
  '[Grid] - Set Dropdown List Values',
  props<{ id: string; column: string; list: DropDownList }>()
);
